import React from "react";

export interface TranscriptEntry {
  role: string;
  text: string;
  timestamp?: string;
  toolCall?: { toolName?: string; arguments?: Record<string, unknown> };
  toolResult?: { result?: unknown; error?: string };
}

function formatTime(ts?: string) {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * What each tool looks like to the organisation reading the transcript.
 *
 * An allow-list, not a lookup with a fallback: tool names and their raw results are our internals
 * (they carry ids, provider-shaped payloads and argument dumps), and an organisation should only
 * ever see the business action that took place. Anything unrecognised — including any tool added
 * later — renders nothing rather than leaking a name we never chose to expose.
 */
const TOOL_LABELS: Record<string, string> = {
  lookup_contact: "Looked up the caller's record",
  lookup_property: "Looked up a property",
  save_call_details: "Saved call details",
  update_contact: "Updated the contact",
  schedule_callback: "Scheduled a callback",
  set_outcome: "Recorded the call outcome",
  transfer_call: "Transferred to a colleague",
  record_do_not_contact: "Recorded a do not contact request",
};

function ToolEntry({ entry }: { entry: TranscriptEntry }) {
  const label = TOOL_LABELS[entry.toolCall?.toolName ?? ""];
  if (!label) return null;

  // Deliberately no result or error detail — that a step failed is ours to act on, and a raw
  // payload here would undo the point of the allow-list above.
  return (
    <div className="flex justify-center py-1">
      <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-3 py-1 text-[11px] text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

export default function TranscriptEntryView({ entry }: { entry: TranscriptEntry }) {
  // Every tool entry goes through ToolEntry, including ones with no toolCall attached. Their raw
  // `text` is "<tool_name>: <result>", so falling through to the generic branch below would print
  // exactly what the allow-list exists to keep out.
  if (entry.role === "tool") return <ToolEntry entry={entry} />;

  const isAssistant = entry.role === "assistant";
  const isUser      = entry.role === "user";
  const time        = formatTime(entry.timestamp);

  // A human taking the call over is the most significant thing in a transcript — call it out
  // rather than letting it read as a grey system aside.
  if (entry.text?.startsWith("Call taken over by a team member.")) {
    return (
      <div className="flex justify-center py-1.5">
        <span className="text-[11px] font-medium rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-1">
          {entry.text}
        </span>
      </div>
    );
  }

  if (!isAssistant && !isUser) {
    return (
      <div className="flex justify-center py-0.5">
        <span className="text-[11px] text-muted-foreground italic">[{entry.role}] {entry.text}</span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-0.5 ${isUser ? "items-end" : "items-start"}`}>
      <div
        className={[
          "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
          isAssistant
            ? "rounded-tl-sm bg-muted text-foreground"
            : "rounded-tr-sm bg-primary text-primary-foreground",
        ].join(" ")}
      >
        {entry.text}
      </div>
      {time && (
        <span className="text-[10px] text-muted-foreground px-1">{time}</span>
      )}
    </div>
  );
}
